import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PhoneCall, Plus, Search, Clock } from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";

const SupportCalls = () => {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [callerName, setCallerName] = useState("");
  const [callerPhone, setCallerPhone] = useState("");
  const [callSummary, setCallSummary] = useState("");
  const [callCategory, setCallCategory] = useState("general");
  const [callPriority, setCallPriority] = useState("medium");

  // Use support_tickets with a "call" category to log calls as tickets
  const { data: calls, isLoading } = useQuery({
    queryKey: ["support-calls"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("*")
        .eq("category", "call")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const createCallMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      const { error } = await supabase.from("support_tickets").insert({
        subject: `مكالمة: ${callerName} - ${callerPhone}`,
        description: callSummary,
        category: "call",
        priority: callPriority,
        created_by: user.id,
        status: "resolved",
        resolved_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تم تسجيل المكالمة بنجاح");
      queryClient.invalidateQueries({ queryKey: ["support-calls"] });
      setDialogOpen(false);
      setCallerName("");
      setCallerPhone("");
      setCallSummary("");
      setCallCategory("general");
      setCallPriority("medium");
    },
    onError: () => toast.error("فشل تسجيل المكالمة"),
  });

  const statusLabels: Record<string, string> = {
    open: "بحاجة متابعة",
    in_progress: "جاري المتابعة",
    resolved: "مكتملة",
    closed: "مغلقة",
  };

  const filtered = calls?.filter((c: any) =>
    c.subject?.includes(search) || c.description?.includes(search)
  ) || [];

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <PhoneCall className="w-6 h-6 text-primary" />
            سجل المكالمات
          </h1>
          <p className="text-muted-foreground text-sm mt-1">تسجيل ومتابعة المكالمات الواردة</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2"><Plus className="w-4 h-4" /> تسجيل مكالمة</Button>
          </DialogTrigger>
          <DialogContent dir="rtl">
            <DialogHeader>
              <DialogTitle>تسجيل مكالمة جديدة</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>اسم المتصل</Label>
                  <Input value={callerName} onChange={(e) => setCallerName(e.target.value)} placeholder="اسم المتصل" />
                </div>
                <div className="space-y-2">
                  <Label>رقم الهاتف</Label>
                  <Input value={callerPhone} onChange={(e) => setCallerPhone(e.target.value)} placeholder="05xxxxxxxx" dir="ltr" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>الأولوية</Label>
                <Select value={callPriority} onValueChange={setCallPriority}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">منخفضة</SelectItem>
                    <SelectItem value="medium">متوسطة</SelectItem>
                    <SelectItem value="high">عالية</SelectItem>
                    <SelectItem value="urgent">عاجلة</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>ملخص المكالمة</Label>
                <Textarea
                  value={callSummary}
                  onChange={(e) => setCallSummary(e.target.value)}
                  placeholder="اكتب ملخص المكالمة..."
                  rows={4}
                />
              </div>
              <Button
                className="w-full"
                disabled={!callerName.trim() || !callSummary.trim() || createCallMutation.isPending}
                onClick={() => createCallMutation.mutate()}
              >
                {createCallMutation.isPending ? "جاري التسجيل..." : "حفظ المكالمة"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><PhoneCall className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">إجمالي المكالمات</p>
              <p className="text-2xl font-bold">{calls?.length || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10"><Clock className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">مكتملة</p>
              <p className="text-2xl font-bold">{calls?.filter((c: any) => c.status === "resolved").length || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10"><Clock className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">تحتاج متابعة</p>
              <p className="text-2xl font-bold">{calls?.filter((c: any) => c.status === "open" || c.status === "in_progress").length || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative w-full max-w-md">
        <Search className="absolute right-3 top-2.5 w-4 h-4 text-muted-foreground" />
        <Input placeholder="بحث في المكالمات..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">المكالمات ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">جاري التحميل...</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <PhoneCall className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground">لا توجد مكالمات مسجلة</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>المتصل</TableHead>
                  <TableHead>الملخص</TableHead>
                  <TableHead>الأولوية</TableHead>
                  <TableHead>الحالة</TableHead>
                  <TableHead>التاريخ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c: any) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-mono text-xs">#{c.ticket_number}</TableCell>
                    <TableCell className="font-medium">{c.subject?.replace("مكالمة: ", "")}</TableCell>
                    <TableCell className="max-w-[250px] truncate text-sm text-muted-foreground">{c.description}</TableCell>
                    <TableCell>
                      <span className={`text-xs font-medium ${
                        c.priority === "urgent" ? "text-destructive" :
                        c.priority === "high" ? "text-amber-600" : "text-muted-foreground"
                      }`}>
                        {c.priority === "urgent" ? "عاجلة" : c.priority === "high" ? "عالية" : c.priority === "medium" ? "متوسطة" : "منخفضة"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs">{statusLabels[c.status] || c.status}</span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {format(new Date(c.created_at), "dd MMM HH:mm", { locale: ar })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SupportCalls;
